/**
 * Workout View Modal Component
 *
 * Universal modal for viewing workout details
 * Accessible from Gym Tracker and Schedule pages
 * Displays:
 * - Workout duration and date
 * - Exercise list with sets
 * - Reps/weight for strength exercises
 * - Notes for cardio/other exercises
 * - Click exercise name to rename
 */

import { useState, useEffect, useCallback, type KeyboardEvent } from 'react';
import { supabase } from '@/lib/supabase';
import './WorkoutViewModal.css';

interface WorkoutSet {
  id: string;
  set_number: number;
  reps: number | null;
  weight: number | null;
  intensity: number | null;
  notes: string | null;
}

interface WorkoutExercise {
  id: string;
  name: string;
  category: string;
  sets: WorkoutSet[];
}

interface WorkoutDetails {
  id: string;
  startedAt: Date;
  completedAt: Date | null;
  workoutType: string;
  exercises: WorkoutExercise[];
}

interface WorkoutViewModalProps {
  workoutId: string;
  onClose: () => void;
}

export function WorkoutViewModal({ workoutId, onClose }: WorkoutViewModalProps): JSX.Element {
  const [workout, setWorkout] = useState<WorkoutDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingExerciseId, setEditingExerciseId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    async function fetchWorkoutDetails(): Promise<void> {
      const { data, error: fetchError } = await supabase
        .from('workout_logs')
        .select(`
          id,
          started_at,
          completed_at,
          notes,
          workout_log_sets (
            id,
            set_number,
            reps,
            weight,
            intensity,
            notes,
            exercise_id,
            exercises (
              id,
              name,
              category
            )
          )
        `)
        .eq('id', workoutId)
        .single();

      if (fetchError) {
        setError('Failed to load workout details');
        setLoading(false);
        return;
      }

      if (!data) {
        setError('Workout not found');
        setLoading(false);
        return;
      }

      // Group sets by exercise
      const exerciseMap = new Map<string, WorkoutExercise>();

      const sets = data.workout_log_sets as unknown as Array<{
        id: string;
        set_number: number;
        reps: number | null;
        weight: number | null;
        intensity: number | null;
        notes: string | null;
        exercise_id: string;
        exercises: { id: string; name: string; category: string };
      }>;

      for (const set of sets) {
        const exerciseId = set.exercise_id;
        const exercise = set.exercises;

        if (!exerciseMap.has(exerciseId)) {
          exerciseMap.set(exerciseId, {
            id: exerciseId,
            name: exercise.name,
            category: exercise.category,
            sets: [],
          });
        }

        exerciseMap.get(exerciseId)?.sets.push({
          id: set.id,
          set_number: set.set_number,
          reps: set.reps,
          weight: set.weight,
          intensity: set.intensity,
          notes: set.notes,
        });
      }

      // Sort sets by set_number within each exercise
      exerciseMap.forEach((exercise) => {
        exercise.sets.sort((a, b) => a.set_number - b.set_number);
      });

      setWorkout({
        id: data.id,
        startedAt: new Date(data.started_at),
        completedAt: data.completed_at ? new Date(data.completed_at) : null,
        workoutType: data.notes ?? 'Workout',
        exercises: Array.from(exerciseMap.values()),
      });
      setLoading(false);
    }

    fetchWorkoutDetails();
  }, [workoutId]);

  const handleRenameExercise = useCallback(async (exerciseId: string, newName: string) => {
    if (!workout) return;

    const trimmed = newName.trim();
    const exercise = workout.exercises.find((e) => e.id === exerciseId);
    if (!trimmed || !exercise || trimmed === exercise.name) {
      setEditingExerciseId(null);
      return;
    }

    // Update in database
    const { error: updateError } = await supabase
      .from('exercises')
      .update({ name: trimmed })
      .eq('id', exerciseId);

    if (updateError) {
      console.error('Failed to rename exercise:', updateError);
      setEditingExerciseId(null);
      return;
    }

    // Update local state
    setWorkout({
      ...workout,
      exercises: workout.exercises.map((ex) =>
        ex.id === exerciseId ? { ...ex, name: trimmed } : ex
      ),
    });
    setEditingExerciseId(null);
  }, [workout]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>, exerciseId: string) => {
    if (e.key === 'Enter') {
      handleRenameExercise(exerciseId, editName);
    } else if (e.key === 'Escape') {
      setEditingExerciseId(null);
    }
  }, [handleRenameExercise, editName]);

  const formatDuration = (start: Date, end: Date | null): string => {
    if (!end) return '—';
    const durationMs = end.getTime() - start.getTime();
    const minutes = Math.floor(durationMs / 60000);
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return hours > 0 ? `${hours}h ${remainingMinutes}m` : `${minutes}m`;
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const renderStars = (intensity: number): string => {
    return '⭐'.repeat(intensity);
  };

  const isCardioOrOther = (category: string): boolean => {
    return category === 'cardio' || category === 'other';
  };

  const getTotalSets = (): number => {
    return workout?.exercises.reduce((sum, ex) => sum + ex.sets.length, 0) ?? 0;
  };

  const getTotalVolume = (): number => {
    return workout?.exercises.reduce((sum, ex) => {
      return sum + ex.sets.reduce((setSum, set) => {
        return setSum + (set.weight ?? 0) * (set.reps ?? 0);
      }, 0);
    }, 0) ?? 0;
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="workout-view-modal" onClick={(e) => e.stopPropagation()}>
        <div className="workout-view-modal__header">
          <div className="workout-view-modal__header-content">
            {loading ? (
              <h2 className="workout-view-modal__title">Loading...</h2>
            ) : error ? (
              <h2 className="workout-view-modal__title">Error</h2>
            ) : (
              <>
                <h2 className="workout-view-modal__title">{workout?.workoutType} Day</h2>
                <p className="workout-view-modal__date">
                  {workout && formatDate(workout.startedAt)}
                </p>
              </>
            )}
          </div>
          <button type="button" className="workout-view-modal__close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="workout-view-modal__content">
          {loading && (
            <div className="workout-view-modal__loading">Loading workout details...</div>
          )}

          {error && (
            <div className="workout-view-modal__error">{error}</div>
          )}

          {!loading && !error && workout && (
            <>
              {/* Summary Stats */}
              <div className="workout-view-modal__stats">
                <div className="workout-view-modal__stat">
                  <span className="workout-view-modal__stat-value">
                    {formatDuration(workout.startedAt, workout.completedAt)}
                  </span>
                  <span className="workout-view-modal__stat-label">Duration</span>
                </div>
                <div className="workout-view-modal__stat">
                  <span className="workout-view-modal__stat-value">{getTotalSets()}</span>
                  <span className="workout-view-modal__stat-label">Sets</span>
                </div>
                <div className="workout-view-modal__stat">
                  <span className="workout-view-modal__stat-value">
                    {getTotalVolume().toLocaleString()}
                  </span>
                  <span className="workout-view-modal__stat-label">Volume (lbs)</span>
                </div>
                <div className="workout-view-modal__stat">
                  <span className="workout-view-modal__stat-value">
                    {workout.exercises.length}
                  </span>
                  <span className="workout-view-modal__stat-label">Exercises</span>
                </div>
              </div>

              {/* Exercises List */}
              <div className="workout-view-modal__exercises">
                {workout.exercises.length === 0 ? (
                  <div className="workout-view-modal__empty">No exercises recorded</div>
                ) : (
                  workout.exercises.map((exercise) => (
                    <div key={exercise.id} className="workout-view-modal__exercise">
                      <div className="workout-view-modal__exercise-header">
                        {editingExerciseId === exercise.id ? (
                          <input
                            type="text"
                            className="workout-view-modal__exercise-name-input"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onBlur={() => handleRenameExercise(exercise.id, editName)}
                            onKeyDown={(e) => handleKeyDown(e, exercise.id)}
                            autoFocus
                          />
                        ) : (
                          <h3
                            className="workout-view-modal__exercise-name"
                            onClick={() => {
                              setEditName(exercise.name);
                              setEditingExerciseId(exercise.id);
                            }}
                            title="Click to rename"
                            style={{ cursor: 'pointer' }}
                          >
                            {exercise.name}
                          </h3>
                        )}
                        <span className="workout-view-modal__exercise-category">
                          {exercise.category}
                        </span>
                      </div>

                      <div className="workout-view-modal__sets">
                        {exercise.sets.map((set) => (
                          <div key={set.id} className="workout-view-modal__set">
                            <span className="workout-view-modal__set-number">
                              Set {set.set_number}
                            </span>
                            <div className="workout-view-modal__set-details">
                              {isCardioOrOther(exercise.category) ? (
                                // Cardio/Other: Show notes or "Completed"
                                <span className="workout-view-modal__set-value">
                                  {set.notes ?? 'Completed'}
                                </span>
                              ) : (
                                // Strength: Show weight and reps
                                <>
                                  {set.weight !== null && (
                                    <span className="workout-view-modal__set-value">
                                      {set.weight} lbs
                                    </span>
                                  )}
                                  {set.weight !== null && set.reps !== null && (
                                    <span className="workout-view-modal__set-separator">×</span>
                                  )}
                                  {set.reps !== null && (
                                    <span className="workout-view-modal__set-value">
                                      {set.reps} reps
                                    </span>
                                  )}
                                  {set.reps === null && set.weight === null && (
                                    <span className="workout-view-modal__set-value">Completed</span>
                                  )}
                                </>
                              )}
                              {set.intensity && (
                                <span className="workout-view-modal__set-intensity">
                                  {renderStars(set.intensity)}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        <div className="workout-view-modal__footer">
          <button
            type="button"
            className="workout-view-modal__button"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
